import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { ControlledField, colors, Vehicle } from 'remote/global';
import {
  LineDetailsSchemaType,
  CorporateCharterItinerarySchemaType,
  lineDetailsSchema,
  CorporateCharterScheduleSchemaType,
} from 'components/forms/CorporateCharter/types';
import { useOperatorsVehiclesAllQuery } from 'api/queries/operators';
import PricingTable from './PricingTable';
import AdditionalDeparturesTable from './AdditionalDeparturesTable';
import DatePickerField from './FormFields/DatePickerField';
import DaysOfWeekPickerField from './FormFields/DaysOfWeekPickerField';
import VehicleSelectField from './FormFields/VehicleSelectField';

interface Props {
  itineraryValues: CorporateCharterItinerarySchemaType;
  scheduleValues: CorporateCharterScheduleSchemaType;
  onValid: SubmitHandler<LineDetailsSchemaType>;
  defaultValues?: LineDetailsSchemaType;
  onBack: (data: LineDetailsSchemaType) => void;
}

const getDefaultValues = (
  itineraryValues: CorporateCharterItinerarySchemaType
): LineDetailsSchemaType => {
  const { departures } = itineraryValues;
  const { clientDepartureTime } = departures[0];
  return {
    lineName: '',
    ridePriceExclVat: 0,
    ridePricePerKmEur: 0,
    departures: [
      {
        clientDepartureTime,
        licensePlate: '',
        schedule: {
          daysOfWeek: [],
          startsOn: '',
          endsOn: '',
        },
      },
    ],
  };
};

function LineDetailsForm({
  defaultValues,
  itineraryValues,
  scheduleValues,
  onValid,
  onBack,
}: Props) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const formInstance = useForm<LineDetailsSchemaType>({
    defaultValues: defaultValues ?? getDefaultValues(itineraryValues),
    resolver: zodResolver(lineDetailsSchema),
  });
  const { control, handleSubmit, getValues } = formInstance;
  const { data: allVehicles } = useOperatorsVehiclesAllQuery();
  const elements =
    allVehicles?.filter((vehicle) => vehicle.seats >= Number(itineraryValues.passengerCount)) ?? [];
  const filteredVehicles = elements.map(({ licensePlate }: Vehicle) => licensePlate);

  return (
    <Box
      p='0 24px 24px'
      overflow='auto'
      sx={{
        width: '100%',
        margin: '0 auto',
      }}
    >
      <form onSubmit={handleSubmit(onValid)}>
        <Box
          display='flex'
          justifyContent='flex-start'
          sx={{
            width: '100%',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
          }}
        >
          <Box
            id='corporateCharterLineDetails'
            mr='48px'
            width='100%'
            maxWidth={{
              xs: '100%',
              sm: '370px',
            }}
          >
            <Typography
              sx={{
                color: colors.uiBlue600,
                fontSize: '18px',
                mb: '24px',
                fw: 600,
              }}
            >
              {t('CorporateCharterView.steps.lineDetails.title')}
            </Typography>
            <Box mb='8px'>
              {filteredVehicles && (
                <Box data-testid='charter-vehicle-select'>
                  <VehicleSelectField
                    formInstance={formInstance}
                    name='departures.0.licensePlate'
                    filteredVehicles={filteredVehicles}
                  />
                </Box>
              )}
            </Box>
            <ControlledField control={control} name='lineName' />
          </Box>
          <Box
            id='corporateCharterScheduleDetails'
            width='100%'
            maxWidth={{
              xs: '100%',
              sm: '370px',
            }}
            margin={{
              xs: '24px 0',
              sm: '0',
            }}
          >
            <Typography
              sx={{
                color: colors.uiBlue600,
                fontSize: '18px',
                fw: 600,
                mb: '24px',
              }}
            >
              {t('CorporateCharterView.steps.lineDetails.scheduling.title')}
            </Typography>
            <Box data-testid='charter-daysofweek-select'>
              <DaysOfWeekPickerField
                formInstance={formInstance}
                name='departures.0.schedule.daysOfWeek'
              />
            </Box>
            <DatePickerField formInstance={formInstance} name='departures.0.schedule.startsOn' />
            <DatePickerField formInstance={formInstance} name='departures.0.schedule.endsOn' />
          </Box>
        </Box>
        <PricingTable formInstance={formInstance} scheduleValues={scheduleValues} />
        {filteredVehicles && (
          <AdditionalDeparturesTable
            filteredVehicles={filteredVehicles}
            formInstance={formInstance}
          />
        )}
        <Box
          display='flex'
          alignItems='center'
          width='100%'
          m='24px 0'
          justifyContent='space-between'
          flexDirection={{
            xs: 'column',
            sm: 'row',
          }}
        >
          <Button
            sx={{
              mr: {
                xs: 0,
                sm: '24px',
              },
              mb: {
                xs: '12px',
                sm: 0,
              },
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
            variant='outlined'
            onClick={() => onBack(getValues())}
          >
            {t('back')}
          </Button>
          <Button
            disabled={formInstance.formState.isSubmitting}
            type='submit'
            sx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
          >
            {t('continue')}
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default LineDetailsForm;
